import {
  Button,
  Chip,
  DataTable,
  DateRangePicker,
  Dot,
  Drawer,
  FilterBar,
  PageHead,
  Pager,
  Popover,
  Segmented,
  StateBlock,
  resolveRangePreset,
  type ActiveFilter,
  type Column,
  type DateRangePresetId,
} from '@w7/ui'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useBookTicker, useBookTickerFilterOptions } from '@/api/hooks/book/useBookTicker'
import {
  TICKER_FLAG_COLORS,
  useBettickerMapping,
} from '@/api/hooks/book/useBettickerMapping'
import { hasLiveApi } from '@/api/client'
import { ago, hms, indianCurrency, ll, lll, odds, tickerAmt } from '@/lib/format'
import { formatLineSessionDisplayRun } from '@/lib/lineSessionDisplayRun'
import { isLineSessionBetListMarket } from '@/features/net-exposure/workstation/lineSessionBetOdds'
import { tickerProfitLiability } from './profit'
import { TickerMemberSearch } from './TickerMemberSearch'
import { TickerMemberCell } from './TickerMemberCell'
import { BETFAIR_REASON_LEGEND } from './betfairReason'
import {
  MembersIpMappingModal,
  MembersMappingModal,
} from './MembersMappingModal'
import {
  bqFilterCascade,
  filterOptionsBackendForMode,
  tickerBackendForMode,
} from './tickerBackend'
import { dataSourceLabel, resolveDataSource } from '../dataSource'
import {
  DEFAULT_TICKER_FILTERS,
  PREVIOUS_RANGE_MAX_DAYS,
  SOURCE_LABEL,
  TICKER_MARKET_TYPES,
  TICKER_PAGE_SIZES,
  WORKSPACE_SOURCE,
  tickerStatusLabel,
  tickerStatusesForScope,
  type BookWorkspaceId,
  type TickerBet,
  type TickerFilters,
} from '../domain'
import { workspaceById, type ModeDescriptor } from '../routes'
import {
  defaultPreviousRange,
  filtersAreActive,
  moreFilterCount,
  parseTickerFilters,
  cascadeEventPatch,
  cascadeSportPatch,
  patchTickerFilters,
  previousRangeMissing,
  serializeTickerFilters,
} from './filters'

/** Presets Previous can honour — capped at the API's 31-day window. */
const PREVIOUS_PRESETS: DateRangePresetId[] = [
  'today',
  'yesterday',
  'this-week',
  'last-week',
  'last-7',
  'last-30',
  'custom',
]

/** Betfair line session: Odds Placed / Odds Matched hold line — Back ceil / Lay floor. */
export function tickerOddsPlaced(bet: TickerBet): string {
  if (isLineSessionBetListMarket(bet.marketName) && bet.oddsPlaced > 5) {
    return formatLineSessionDisplayRun(bet.oddsPlaced, bet.side) || odds(bet.oddsPlaced)
  }
  return odds(bet.oddsPlaced)
}

export function tickerOddsMatched(bet: TickerBet): string {
  if (isLineSessionBetListMarket(bet.marketName) && bet.oddsMatched > 5) {
    return formatLineSessionDisplayRun(bet.oddsMatched, bet.side) || odds(bet.oddsMatched)
  }
  return odds(bet.oddsMatched)
}

/** Lab bet-ticker: `I - ravi_07`. Other tickers used `ravi_07 - INR`. */
function memberLabel(row: TickerBet): string {
  const name = row.memberUsername || row.memberId || '—'
  const code = row.currency?.trim()
  if (!code) return name
  return `${code.charAt(0).toUpperCase()} - ${name}`
}

/** Local-parts day label — never `Date.parse` an ISO day as UTC midnight. */
function llDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  return y && m && d ? ll(new Date(y, m - 1, d)) : ll(iso)
}

function rangeSummary(from: string, to: string): string {
  if (!from || !to) return ''
  return from === to ? llDay(from) : `${llDay(from)} – ${llDay(to)}`
}

function presetOf(from: string, to: string): DateRangePresetId {
  for (const id of PREVIOUS_PRESETS) {
    const resolved = resolveRangePreset(id)
    if (resolved && resolved.from === from && resolved.to === to) return id
  }
  return 'custom'
}

/**
 * Dates are required on Previous, so they must not count as "narrowing" or the
 * empty state and Clear action fire on a blank default week.
 */
function filtersNarrowResults(filters: TickerFilters, previous: boolean): boolean {
  if (!previous) return filtersAreActive(filters)
  const d = DEFAULT_TICKER_FILTERS
  return (
    filters.members.length > 0 ||
    filters.search !== d.search ||
    filters.sport !== d.sport ||
    Boolean(filters.event) ||
    Boolean(filters.market) ||
    filters.marketType !== d.marketType ||
    filters.side !== d.side ||
    filters.status !== d.status ||
    filters.currency !== d.currency ||
    filters.stakeMin != null ||
    filters.stakeMax != null
  )
}

function BetDetail({ bet }: { bet: TickerBet }) {
  const isLay = bet.side === 'LAY'
  const side = isLay ? 'lay' : 'back'

  return (
    <div className="w7-bm-bet">
      <article className="w7-bm-bet-hero" data-side={side}>
        <div className="w7-bm-bet-hero-rail" aria-hidden="true" />
        <div className="w7-bm-bet-hero-body">
          <div className="w7-bm-bet-chips" role="list">
            <span className="w7-bm-side-badge" data-side={side} role="listitem">
              {isLay ? 'Lay' : 'Back'}
            </span>
            <span role="listitem">
              <Chip size="sm" tone="muted">
                {SOURCE_LABEL[bet.source]}
              </Chip>
            </span>
          </div>

          <p className="w7-bm-bet-event">{bet.eventName || '—'}</p>
          <p className="w7-bm-bet-market">{bet.marketName || '—'}</p>
          <p className="w7-bm-bet-selection">
            <span className="w7-bm-bet-selection-kicker">Selection</span>
            {bet.selection || '—'}
          </p>

          <div className="w7-bm-bet-metrics">
            <div className="w7-bm-bet-metric">
              <span className="w7-bm-bet-metric-label">Odds Placed</span>
              <strong className="w7-bm-bet-metric-value">
                {tickerOddsPlaced(bet)}
              </strong>
            </div>
            <div className="w7-bm-bet-metric">
              <span className="w7-bm-bet-metric-label">Odds Matched</span>
              <strong className="w7-bm-bet-metric-value">
                {tickerOddsMatched(bet)}
              </strong>
            </div>
          </div>
        </div>
      </article>

      <dl className="w7-bm-bet-facts">
        <div className="w7-bm-bet-fact">
          <dt>Matched</dt>
          <dd>{tickerAmt(bet.matchedAmount)}</dd>
        </div>
        <div className="w7-bm-bet-fact">
          <dt>Unmatched</dt>
          <dd>{tickerAmt(bet.unmatchedAmount)}</dd>
        </div>
        <div className="w7-bm-bet-fact">
          <dt>Placed</dt>
          <dd>{bet.placedAt ? lll(bet.placedAt) : '—'}</dd>
        </div>
        <div className="w7-bm-bet-fact">
          <dt>Matched at</dt>
          <dd>{bet.matchedAt ? lll(bet.matchedAt) : '—'}</dd>
        </div>
        <div className="w7-bm-bet-fact w7-bm-bet-fact--wide">
          <dt>IP</dt>
          <dd className="w7-bm-detail-ip">{bet.clientIp ?? '—'}</dd>
        </div>
        <div className="w7-bm-bet-fact w7-bm-bet-fact--wide">
          <dt>Bet id</dt>
          <dd className="w7-bm-detail-ip">{bet.id}</dd>
        </div>
      </dl>
    </div>
  )
}

function MoreFilters({
  previous,
  filters,
  setFilters,
  events,
  markets,
}: {
  previous: boolean
  filters: TickerFilters
  setFilters: (patch: Partial<TickerFilters>) => void
  events: string[]
  markets: string[]
}) {
  const anchor = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const count = moreFilterCount(filters, previous)

  return (
    <>
      <Button
        ref={anchor}
        variant={count > 0 || open ? 'secondary' : 'ghost'}
        icon="filter"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        {count > 0 ? `More (${count})` : 'More'}
      </Button>
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchor}
        label="More filters"
        align="end"
        width={340}
      >
        <div className="w7-bm-more">
          <label className="w7-bm-more-field">
            <span>Event</span>
            <select
              className="w7-bm-select w7-bm-select--wide"
              aria-label="Event"
              value={filters.event}
              onChange={(event) =>
                setFilters(cascadeEventPatch(event.target.value))
              }
            >
              <option value="">All events</option>
              {events.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label className="w7-bm-more-field">
            <span>Market Types</span>
            <select
              className="w7-bm-select w7-bm-select--wide"
              aria-label="Market Types"
              value={filters.marketType}
              onChange={(event) =>
                setFilters({
                  marketType: event.target.value as typeof filters.marketType,
                })
              }
            >
              <option value="all">All market types</option>
              {TICKER_MARKET_TYPES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          <label className="w7-bm-more-field">
            <span>Market</span>
            <select
              className="w7-bm-select w7-bm-select--wide"
              aria-label="Market"
              value={filters.market}
              onChange={(event) => setFilters({ market: event.target.value })}
            >
              <option value="">All markets</option>
              {markets.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </label>
          {!previous ? (
            <div className="w7-bm-more-row">
              <label className="w7-bm-more-field">
                <span>From</span>
                <input
                  className="w7-bm-input w7-bm-input--wide"
                  type="date"
                  aria-label="From date"
                  value={filters.from}
                  onChange={(event) => setFilters({ from: event.target.value })}
                />
              </label>
              <label className="w7-bm-more-field">
                <span>To</span>
                <input
                  className="w7-bm-input w7-bm-input--wide"
                  type="date"
                  aria-label="To date"
                  value={filters.to}
                  onChange={(event) => setFilters({ to: event.target.value })}
                />
              </label>
            </div>
          ) : null}
          <div className="w7-bm-more-row">
            <label className="w7-bm-more-field">
              <span>Min stake</span>
              <input
                className="w7-bm-input w7-bm-input--wide"
                type="number"
                min={0}
                inputMode="decimal"
                aria-label="Minimum stake"
                placeholder="0"
                value={filters.stakeMin ?? ''}
                onChange={(event) =>
                  setFilters({
                    stakeMin: event.target.value === '' ? null : Number(event.target.value),
                  })
                }
              />
            </label>
            <label className="w7-bm-more-field">
              <span>Max stake</span>
              <input
                className="w7-bm-input w7-bm-input--wide"
                type="number"
                min={0}
                inputMode="decimal"
                aria-label="Maximum stake"
                placeholder="0"
                value={filters.stakeMax ?? ''}
                onChange={(event) =>
                  setFilters({
                    stakeMax: event.target.value === '' ? null : Number(event.target.value),
                  })
                }
              />
            </label>
          </div>
        </div>
      </Popover>
    </>
  )
}

export function TickerView({
  workspace,
  mode,
}: {
  workspace: BookWorkspaceId
  mode: ModeDescriptor
}) {
  const [params, setParams] = useSearchParams()
  const [selected, setSelected] = useState<TickerBet | null>(null)
  const [memberDraft, setMemberDraft] = useState('')

  const filters = useMemo(() => parseTickerFilters(params), [params])
  const source = WORKSPACE_SOURCE[workspace]
  const previous = mode.id === 'previous' || mode.id === 'previous-new'
  /** Lab Betfair 7-day switch — BQ when span >7 or toDate ≠ today. */
  const tickerBackend = tickerBackendForMode(mode.id, filters.from, filters.to)
  const needsRange = previous && previousRangeMissing(filters)
  const narrowed = filtersNarrowResults(filters, previous)

  const setFilters = (patch: Partial<TickerFilters>) => {
    const next = patchTickerFilters(filters, patch)
    setParams(serializeTickerFilters(next), { replace: true })
  }

  const resetFilters = () =>
    setFilters(previous ? { ...DEFAULT_TICKER_FILTERS, ...defaultPreviousRange() } : DEFAULT_TICKER_FILTERS)

  useEffect(() => {
    if (!needsRange) return
    const range = defaultPreviousRange()
    setParams(serializeTickerFilters(patchTickerFilters(filters, range)), { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsRange])

  useEffect(() => {
    if (filters.status === 'all') return
    const allowed = tickerStatusesForScope(previous ? 'previous' : 'current')
    if (!allowed.includes(filters.status)) setFilters({ status: 'all' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previous, filters.status])

  const [membersOpen, setMembersOpen] = useState(false)
  const [ipsOpen, setIpsOpen] = useState(false)
  /** Lab Members Name / Ip — title-row actions for anyone on the ticker. */
  const manageFlags = true
  const mappingQ = useBettickerMapping(hasLiveApi())
  const mapping = mappingQ.data

  const ticker = useBookTicker({
    source,
    statusScope: previous ? 'previous' : 'current',
    dataSource: tickerBackend,
    filters,
    enabled: !needsRange,
  })
  // Bets keep tickerBackend (pg for Previous). Pickers use BQ cascade on Previous.
  const filterOptionsBackend = filterOptionsBackendForMode(mode.id)
  const cascade = bqFilterCascade({
    dataSource: filterOptionsBackend,
    sport: filters.sport,
    event: filters.event,
  })
  const options = useBookTickerFilterOptions({
    source,
    statusScope: previous ? 'previous' : 'current',
    dataSource: filterOptionsBackend,
    from: filters.from || undefined,
    to: filters.to || undefined,
    eventType: cascade.eventType,
    eventName: cascade.eventName,
    enabled: !needsRange,
  })
  const page = ticker.data
  const rows = page?.rows ?? []
  const total = page?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / filters.pageSize) || 1)
  const safePage = Math.min(Math.max(1, filters.page), pageCount)
  const sports = options.data?.sports ?? []
  const events = options.data?.events ?? []
  const markets = options.data?.markets ?? []
  const asOf = page?.asOf
  const fixtureMemberSuggestions = useMemo(
    () =>
      [...new Set(rows.map((row) => row.memberUsername).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b),
      ),
    [rows],
  )

  useEffect(() => {
    if (needsRange || !ticker.isSuccess) return
    if (filters.page === safePage) return
    setFilters({ page: safePage })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsRange, ticker.isSuccess, filters.page, safePage])

  const activeFilters = useMemo((): ActiveFilter[] => {
    const chips: ActiveFilter[] = []
    if (filters.search) {
      chips.push({
        id: 'q',
        label: `Member: ${filters.search}`,
        removeLabel: 'Remove member username filter',
        onRemove: () => setFilters({ search: '' }),
      })
    }
    if (filters.members.length > 0) {
      chips.push({
        id: 'members',
        label: `Members: ${filters.members.length} selected`,
        removeLabel: 'Clear members',
        onRemove: () => setFilters({ members: [] }),
      })
    }
    if (filters.sport && filters.sport !== 'all') {
      chips.push({
        id: 'sport',
        label: `Sub Games: ${filters.sport}`,
        removeLabel: 'Remove sub games filter',
        onRemove: () => setFilters({ sport: 'all' }),
      })
    }
    if (filters.event) {
      chips.push({
        id: 'event',
        label: `Event: ${filters.event}`,
        removeLabel: 'Remove event filter',
        onRemove: () => setFilters({ event: '' }),
      })
    }
    if (filters.market) {
      chips.push({
        id: 'market',
        label: `Market: ${filters.market}`,
        removeLabel: 'Remove market filter',
        onRemove: () => setFilters({ market: '' }),
      })
    }
    if (filters.marketType !== 'all') {
      chips.push({
        id: 'marketType',
        label: `Market Types: ${filters.marketType}`,
        removeLabel: 'Clear market types filter',
        onRemove: () => setFilters({ marketType: 'all' }),
      })
    }
    if (filters.side !== 'all') {
      chips.push({
        id: 'side',
        label: filters.side === 'BACK' ? 'Back only' : 'Lay only',
        removeLabel: 'Show back and lay',
        onRemove: () => setFilters({ side: 'all' }),
      })
    }
    if (filters.status !== 'all') {
      chips.push({
        id: 'status',
        label: `Status: ${tickerStatusLabel(filters.status)}`,
        removeLabel: 'Clear status filter',
        onRemove: () => setFilters({ status: 'all' }),
      })
    }
    if (!previous && (filters.from || filters.to)) {
      chips.push({
        id: 'range',
        label: `Placed: ${rangeSummary(filters.from, filters.to) || 'custom'}`,
        removeLabel: 'Clear placed date filter',
        onRemove: () => setFilters({ from: '', to: '' }),
      })
    }
    if (filters.stakeMin != null || filters.stakeMax != null) {
      const lo = filters.stakeMin != null ? indianCurrency(filters.stakeMin, 2) : '…'
      const hi = filters.stakeMax != null ? indianCurrency(filters.stakeMax, 2) : '…'
      chips.push({
        id: 'stake',
        label: `Stake ${lo}–${hi}`,
        removeLabel: 'Clear stake range',
        onRemove: () => setFilters({ stakeMin: null, stakeMax: null }),
      })
    }
    return chips
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, previous])

  const columns: Column<TickerBet>[] = [
    {
      id: 'when',
      header: 'Place time',
      // Time only (24h + seconds) — date lives in range filters / detail drawer.
      cell: (row) =>
        row.placedAt ? (
          <span className="w7-bm-placed w7-mono">{hms(row.placedAt)}</span>
        ) : (
          '—'
        ),
      sortValue: (row) => row.placedAt ?? '',
    },
    {
      id: 'matchedAt',
      header: 'Matched time',
      cell: (row) =>
        row.matchedAt ? (
          <span className="w7-bm-placed w7-mono">{hms(row.matchedAt)}</span>
        ) : (
          '-'
        ),
      sortValue: (row) => row.matchedAt ?? '',
      hideBelow: 900,
    },
    {
      id: 'member',
      header: 'Member',
      cell: (row) => (
        // Lab tr ng-class — marker classes drive whole-row CSS :has() colors.
        // No smart/cron green (Lab CM-only; Agent mapping has no Smart Members).
        <TickerMemberCell
          username={row.memberUsername}
          clientIp={row.clientIp}
          label={memberLabel(row)}
          mapping={mapping}
          doubtfull={row.doubtfull}
          watchlist={row.watchlist}
          isFlagged={row.isFlagged}
          isBetfairFlagged={row.isBetfairFlagged}
          reason={row.reason}
          title={
            row.currency
              ? `${row.memberUsername || row.memberId} — ${row.currency}`
              : undefined
          }
        />
      ),
      sortValue: (row) => row.memberUsername,
    },
    {
      id: 'event',
      header: 'Event',
      cell: (row) => <span className="w7-bm-event-name">{row.eventName || '—'}</span>,
      sortValue: (row) => row.eventName,
    },
    {
      id: 'market',
      header: 'Market',
      cell: (row) => <span className="w7-bm-market-col">{row.marketName || '—'}</span>,
      sortValue: (row) => row.marketName,
      hideBelow: 800,
    },
    {
      id: 'sel',
      header: 'Selection',
      cell: (row) => <span className="w7-bm-selection">{row.selection}</span>,
    },
    {
      id: 'side',
      header: 'Side',
      cell: (row) => (
        <Chip size="sm" tone={row.side === 'LAY' ? 'lay' : 'back'}>
          {row.side}
        </Chip>
      ),
      hideBelow: 720,
    },
    // Lab / CM: Odds Placed + Odds Matched after Side (Stake column removed).
    {
      id: 'oddsPlaced',
      header: 'Odds Placed',
      numeric: true,
      cell: (row) => (
        <span className="w7-bm-odds">{tickerOddsPlaced(row)}</span>
      ),
      sortValue: (row) => row.oddsPlaced,
    },
    {
      id: 'oddsMatched',
      header: 'Odds Matched',
      numeric: true,
      cell: (row) => (
        <span className="w7-bm-odds">{tickerOddsMatched(row)}</span>
      ),
      sortValue: (row) => row.oddsMatched,
    },
    {
      id: 'matched',
      header: 'Matched',
      numeric: true,
      cell: (row) => tickerAmt(row.matchedAmount),
      sortValue: (row) => row.matchedAmount,
      hideBelow: 720,
    },
    {
      id: 'unmatched',
      header: 'Unmatched',
      numeric: true,
      cell: (row) => tickerAmt(row.unmatchedAmount),
      sortValue: (row) => row.unmatchedAmount,
      hideBelow: 900,
    },
    {
      id: 'pl',
      header: 'Profit/Liability',
      numeric: true,
      cell: (row) => {
        const value = tickerProfitLiability(row.oddsMatched, row.matchedAmount, {
          lineSession: isLineSessionBetListMarket(row.marketName),
          oddsPlaced: row.oddsPlaced,
        })
        // Lab: BACK = balance-up, LAY = balance-down (side tone, not sign).
        const tone = row.side === 'LAY' ? 'loss' : 'profit'
        return (
          <span className={`w7-bm-pl w7-bm-pl--${tone}`}>
            {row.matchedAmount ? indianCurrency(value, 2) : '-'}
          </span>
        )
      },
      sortValue: (row) =>
        tickerProfitLiability(row.oddsMatched, row.matchedAmount, {
          lineSession: isLineSessionBetListMarket(row.marketName),
          oddsPlaced: row.oddsPlaced,
        }),
      hideBelow: 1000,
    },
    {
      id: 'multiplier',
      header: 'Multiplier',
      numeric: true,
      cell: (row) => row.multiplier || 1,
      sortValue: (row) => row.multiplier || 1,
      hideBelow: 1100,
    },
  ]

  const workspaceLabel = workspaceById(workspace).label
  const dataSource = resolveDataSource()

  return (
    <>
      <PageHead
        title={
          <span className="w7-bm-title">
            <span className="w7-bm-title-workspace">{workspaceLabel}</span>
            <span className="w7-bm-title-sep" aria-hidden="true">
              /
            </span>
            {mode.label}
          </span>
        }
        actions={
          <>
            {manageFlags ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setMembersOpen(true)}
                data-testid="members-name-btn"
              >
                Members Name
              </Button>
            ) : null}
            {manageFlags ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIpsOpen(true)}
                data-testid="members-ip-btn"
              >
                Members Ip
              </Button>
            ) : null}
            {dataSource === 'mock' ? (
              <Chip tone="warn" size="sm" data-testid="bm-data-source" role="status" aria-live="polite">
                {dataSourceLabel(dataSource)}
              </Chip>
            ) : null}
          </>
        }
      />

      <div className="w7-bm-body">
        <div className="w7-bm-ticker" data-testid="ticker-workspace">
          <div className="w7-bm-toolbar">
            <FilterBar
              label={`${mode.label} filters`}
              active={activeFilters}
              onClearAll={narrowed ? resetFilters : undefined}
              status={
                narrowed
                  ? `Showing ${total} bets with ${activeFilters.length} filters applied`
                  : `Showing ${total} bets`
              }
            >
              {/* Row 1: CM member multi-select · live + refresh */}
              <div className="w7-bm-toolbar-primary">
                <TickerMemberSearch
                  selected={filters.members}
                  draft={memberDraft}
                  onDraftChange={setMemberDraft}
                  fixtureSuggestions={fixtureMemberSuggestions}
                  onAdd={(name) => {
                    if (filters.members.includes(name)) return
                    setFilters({ members: [...filters.members, name], search: '' })
                  }}
                  onRemove={(name) =>
                    setFilters({ members: filters.members.filter((m) => m !== name) })
                  }
                  onClear={() => setFilters({ members: [], search: '' })}
                  onEnter={() => {
                    const name = memberDraft.trim()
                    if (!name) return
                    if (!filters.members.includes(name)) {
                      setFilters({ members: [...filters.members, name], search: '' })
                    }
                    setMemberDraft('')
                  }}
                />
                <span className="w7-bm-toolbar-actions">
                  <span className="w7-bm-toolbar-meta" aria-live="polite">
                    {previous ? (
                      <Chip size="sm" tone="muted" icon="clock">
                        Settled
                      </Chip>
                    ) : (
                      <span className="w7-bm-live">
                        <Dot tone="info" pulse />
                        <span>Live</span>
                      </span>
                    )}
                    {!needsRange && !ticker.isError ? (
                      <span className="w7-bm-toolbar-count">
                        {ticker.isLoading && total === 0 ? '…' : total.toLocaleString('en-IN')}
                      </span>
                    ) : null}
                    {asOf && !previous ? (
                      <span className="w7-bm-toolbar-asof" title={lll(asOf)}>
                        {ago(asOf)}
                      </span>
                    ) : null}
                  </span>
                  <Button
                    variant="ghost"
                    icon="refresh"
                    busy={ticker.isFetching}
                    aria-label="Refresh"
                    onClick={() => void ticker.refetch()}
                  >
                    Refresh
                  </Button>
                </span>
              </div>
              {/* Row 2: remaining filters */}
              <div
                className={
                  previous
                    ? 'w7-bm-toolbar-filters'
                    : 'w7-bm-toolbar-filters w7-bm-toolbar-filters--live'
                }
              >
                <select
                  className="w7-bm-select"
                  aria-label="Sub Games"
                  value={filters.sport}
                  onChange={(event) =>
                    setFilters(cascadeSportPatch(event.target.value))
                  }
                >
                  <option value="all">All sports</option>
                  {sports.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                <Segmented
                  label="Side"
                  value={filters.side}
                  onChange={(side) => setFilters({ side })}
                  items={[
                    { id: 'all', label: 'All' },
                    { id: 'BACK', label: 'Back' },
                    { id: 'LAY', label: 'Lay' },
                  ]}
                />
                <select
                  className="w7-bm-select w7-bm-select--status"
                  aria-label="Status"
                  value={filters.status}
                  onChange={(event) => setFilters({ status: event.target.value })}
                >
                  <option value="all">All status</option>
                  {tickerStatusesForScope(previous ? 'previous' : 'current').map((status) => (
                    <option key={status} value={status}>
                      {tickerStatusLabel(status)}
                    </option>
                  ))}
                </select>
                {previous ? (
                  <DateRangePicker
                    label="Date range"
                    preset={presetOf(filters.from, filters.to)}
                    value={{ from: filters.from, to: filters.to }}
                    summary={rangeSummary(filters.from, filters.to)}
                    presets={PREVIOUS_PRESETS}
                    maxSpanDays={PREVIOUS_RANGE_MAX_DAYS}
                    onChange={(next) => setFilters({ from: next.from, to: next.to })}
                  />
                ) : null}
                <MoreFilters
                  previous={previous}
                  filters={filters}
                  setFilters={setFilters}
                  events={events}
                  markets={markets}
                />
              </div>
            </FilterBar>
        <div className="w7-bm-map-legend" aria-label="Bet ticker flag legend">
          {BETFAIR_REASON_LEGEND.map(({ reason, color }) => (
            <span key={reason} className="w7-bm-map-legend-item">
              <span
                className="w7-bm-map-dot"
                style={{ background: color }}
                aria-hidden="true"
              />
              {reason}
            </span>
          ))}
          {(
            [
              ['watchlist', 'Watchlist Members'],
              ['doubtfull', 'Doubtfull Members'],
            ] as const
          ).map(([key, label]) => (
            <span key={key} className="w7-bm-map-legend-item">
              <span
                className="w7-bm-map-dot"
                style={{ background: TICKER_FLAG_COLORS[key] }}
                aria-hidden="true"
              />
              {label}
            </span>
          ))}
        </div>
      </div>

      {needsRange ? (
        <StateBlock icon="clock" title="Choose a date range">
          Previous bets are read for a date range of up to {PREVIOUS_RANGE_MAX_DAYS} days.
        </StateBlock>
      ) : ticker.isError ? (
        <StateBlock
          icon="warning"
          title="Could not load bets"
          tone="error"
          actions={
            <Button variant="secondary" onClick={() => void ticker.refetch()}>
              Try again
            </Button>
          }
        >
          {ticker.error instanceof Error ? ticker.error.message : 'The bet service did not answer.'}
        </StateBlock>
      ) : ticker.isLoading && rows.length === 0 ? (
        <div className="w7-bm-results">
          <div className="w7-bm-table-wrap">
            <DataTable
              label={`${SOURCE_LABEL[source]} ${mode.label}`}
              rows={[]}
              columns={columns}
              rowKey={(row) => row.id}
              density="compact"
              stickyOffset={0}
              pinLastColumn={false}
              maxHeight="var(--w7-bm-table-h)"
              loading
              empty={null}
            />
          </div>
        </div>
      ) : total === 0 ? (
        <div className="w7-bm-empty-panel" data-testid="ticker-empty">
          <StateBlock
            icon={narrowed ? 'filter' : 'activity'}
            title={
              narrowed
                ? 'No bets match these filters'
                : previous
                  ? 'No settled bets in this range'
                  : 'No open bets right now'
            }
            actions={
              <>
                {narrowed ? (
                  <Button variant="primary" icon="close" onClick={resetFilters}>
                    Clear filters
                  </Button>
                ) : null}
                <Button
                  variant="secondary"
                  icon="refresh"
                  busy={ticker.isFetching}
                  onClick={() => void ticker.refetch()}
                >
                  Refresh
                </Button>
              </>
            }
          >
            {narrowed
              ? 'Widen the search, clear a member or date, or reset filters to see the full ticker again.'
              : previous
                ? 'Try a wider date range (up to 31 days) or pick another member.'
                : 'When members place open sportsbook bets, they will show up here newest first.'}
          </StateBlock>
        </div>
      ) : (
        <div className="w7-bm-results">
          <div className="w7-bm-table-wrap">
            <DataTable
              label={`${SOURCE_LABEL[source]} ${mode.label}`}
              rows={rows}
              columns={columns}
              rowKey={(row) => row.id}
              density="compact"
              stickyOffset={0}
              pinLastColumn={false}
              maxHeight="var(--w7-bm-table-h)"
              loading={ticker.isFetching && rows.length === 0}
              onRowClick={setSelected}
              empty={null}
            />
          </div>

          <div className="w7-bm-pager">
            <select
              className="w7-bm-select"
              aria-label="Rows per page"
              value={String(filters.pageSize)}
              onChange={(event) => setFilters({ pageSize: Number(event.target.value) })}
            >
              {TICKER_PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size} / page
                </option>
              ))}
            </select>
            <Pager
              page={safePage}
              pageCount={pageCount}
              total={total}
              pageSize={filters.pageSize}
              onChange={(next) => setFilters({ page: next })}
            />
          </div>
        </div>
      )}

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected ? selected.memberUsername || selected.memberId : 'Bet'}
        subtitle={
          selected
            ? `${selected.side === 'LAY' ? 'Lay' : 'Back'}${
                    selected.placedAt ? ` · ${ago(selected.placedAt)}` : ''
                  }`
            : undefined
        }
      >
        {selected ? <BetDetail bet={selected} /> : null}
      </Drawer>

          {manageFlags ? (
            <MembersMappingModal open={membersOpen} onClose={() => setMembersOpen(false)} />
          ) : null}
          {manageFlags ? (
            <MembersIpMappingModal open={ipsOpen} onClose={() => setIpsOpen(false)} />
          ) : null}
        </div>
      </div>
    </>
  )
}
